import { useCallback, useRef } from 'react'

const APP_ID = 'fh68jelt'
const API_BASE = 'https://api-iam.intercom.io'

interface IntercomUser {
  user_id?: string
  email?: string
  name?: string
  created_at?: number
  [key: string]: any
}

interface IntercomSettings extends IntercomUser {
  app_id: string
  api_base: string
  hide_default_launcher?: boolean
}

declare global {
  interface Window {
    Intercom: any
    intercomSettings: IntercomSettings
  }
}

export function useIntercom() {
  const isBooted = useRef(false)

  // Boot Intercom with user data
  const boot = useCallback((userData?: IntercomUser) => {
    const settings: IntercomSettings = {
      app_id: APP_ID,
      api_base: API_BASE,
      hide_default_launcher: true,
      ...userData,
    }

    window.intercomSettings = settings
    window.Intercom('boot', settings)
    isBooted.current = true
  }, [])

  // Update Intercom (call when route changes)
  const update = useCallback((userData?: IntercomUser) => {
    if (!window.Intercom || !isBooted.current) return

    if (userData) {
      window.intercomSettings = {
        ...window.intercomSettings,
        ...userData,
      }
    }

    window.Intercom('update', window.intercomSettings)
  }, [])

  // Shutdown Intercom (call on logout)
  const shutdown = useCallback(() => {
    if (!window.Intercom) return

    window.Intercom('shutdown')
    isBooted.current = false
  }, [])

  // Show messenger
  const show = useCallback(() => {
    if (!window.Intercom) return
    window.Intercom('show')
  }, [])

  // Hide messenger
  const hide = useCallback(() => {
    if (!window.Intercom) return
    window.Intercom('hide')
  }, [])

  // Show new message composer
  const showNewMessage = useCallback((message?: string) => {
    if (!window.Intercom) return
    window.Intercom('showNewMessage', message)
  }, [])

  // Track event
  const trackEvent = useCallback((eventName: string, metadata?: any) => {
    if (!window.Intercom) return
    window.Intercom('trackEvent', eventName, metadata)
  }, [])

  return {
    boot,
    update,
    shutdown,
    show,
    hide,
    showNewMessage,
    trackEvent,
    isBooted: () => isBooted.current,
  }
}
